import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { API_URL } from '../utils/apiConfig';

const API = API_URL;

const Notifications = () => {
  const { user: authUser } = useAuth();
  const { user: unifiedUser } = useUnifiedAuth();
  const { t, isRTL } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  // Use whichever auth system has a user
  const user = unifiedUser || authUser;

  // Get session token from localStorage
  const getAuthHeaders = () => {
    const token = localStorage.getItem('unified_session_token');
    if (!token) {
      console.warn('⚠️ No session token found for notifications');
    }
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    console.log('📋 Notifications component mounted, user:', user ? 'present' : 'not found');
    console.log('📋 Session token:', localStorage.getItem('unified_session_token') ? 'present' : 'missing');
    
    // Fetch notifications if we have a token, even if user object isn't loaded yet
    const token = localStorage.getItem('unified_session_token');
    if (token) {
      fetchNotifications();
    } else if (user) {
      // Fallback: try fetching if user exists but no token found
      fetchNotifications();
    } else {
      console.warn('⚠️ No user or token found, cannot fetch notifications');
      setLoading(false);
    }
  }, [user]);

  const fetchNotifications = async () => {
    try {
      console.log('📋 Fetching notifications from:', `${API}/notifications`);
      const headers = getAuthHeaders();
      console.log('📋 Auth headers:', headers.Authorization ? 'present' : 'missing');
      
      const response = await axios.get(`${API}/notifications`, {
        headers
      });
      
      console.log('✅ Notifications fetched successfully:', response.data.length, 'items');
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.read).length);
      setLoading(false);
    } catch (error) {
      console.error('❌ Error fetching notifications:', error.response?.status, error.response?.data || error.message);
      if (error.response?.status === 401) {
        console.error('❌ Authentication failed - token may be invalid or expired');
      }
      setNotifications([]);
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: getAuthHeaders()
      });
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: getAuthHeaders()
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, {
        headers: getAuthHeaders()
      });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setUnreadCount(prev => {
        const notification = notifications.find(n => n.id === notificationId);
        return notification && !notification.read ? prev - 1 : prev;
      });
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'appointment_reminder':
        return '⏰';
      case 'appointment_confirmed':
        return '✅';
      case 'appointment_rescheduled':
        return '📅';
      case 'message':
        return '💬';
      case 'system':
        return '🔔';
      default:
        return '📢';
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-20 bg-white rounded-lg shadow"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className={`flex items-center justify-between mb-6`}>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('notifications')}</h1>
            {unreadCount > 0 && (
              <p className="numeric-ltr text-gray-600 mt-1">
                {unreadCount} unread notification{unreadCount > 1 ? 's' : ''}
              </p>
            )}
          </div>
          
          {notifications.length > 0 && (
            <div className={`flex gap-2`}>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  {t('markAllRead')}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Notifications List */}
        <div className="space-y-4">
          {notifications.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🔔</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('noNotifications')}</h3>
              <p className="text-gray-600">You're all caught up!</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={`bg-white rounded-lg shadow-sm border-l-4 p-4 ${
                  notification.read 
                    ? 'border-gray-200' 
                    : 'border-blue-500 bg-blue-50'
                } hover:shadow-md transition-shadow`}
              >
                <div className={`flex items-start gap-4`}>
                  <div className="text-2xl flex-shrink-0">
                    {getNotificationIcon(notification.type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className={`flex items-start justify-between`}>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">
                          {notification.title}
                        </h3>
                        <p className="text-gray-600 mb-2">
                          {notification.message}
                        </p>
                        <div className={`flex items-center text-sm text-gray-500`}>
                          <span>{formatTimeAgo(notification.created_at)}</span>
                          {!notification.read && (
                            <span className={`w-2 h-2 bg-blue-500 rounded-full ${isRTL ? 'mr-2' : 'ml-2'}`}></span>
                          )}
                        </div>
                      </div>
                      
                      <div className={`flex gap-2`}>
                        {!notification.read && (
                          <button
                            onClick={() => markAsRead(notification.id)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            title="Mark as read"
                          >
                            ✓
                          </button>
                        )}
                        <button
                          onClick={() => deleteNotification(notification.id)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                          title="Delete"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Notifications;